import { useEffect, useState } from 'react';

import { useTranslation } from '@fluxora/i18n';
import { Card, CardBody, Input, Label } from '@fluxora/ui';

import {
  CUSTOM_TIER_ID,
  parseCustomKwh,
  resolveInitialSelection,
  USAGE_TIERS,
  type ConsumptionSelectionId,
} from '../services/consumption';
import { useFunnel } from '../state/FunnelProvider';

/**
 * The consumption section shown below the resolved meter card in BOTH on-ramps
 * (SHOP-4 addition). The shopper confirms how much they use — MONTHLY kWh — as
 * a coarse tier (Low / Medium / High) or a Custom figure; the result is stored
 * in `FunnelProvider.monthlyUsageKwh`, which SHOP-5A prices plans at.
 *
 * ── AUTO-SELECT ────────────────────────────────────────────────────────────
 * On mount we derive the initial selection from the meter's estimated annual
 * usage (`resolveInitialSelection`): the tier it falls in, or Custom pre-filled
 * with the monthly value when it is above 2000/mo.
 *
 * ── LOGIC LIVES IN THE SERVICE ─────────────────────────────────────────────
 * Tier ranges, annual→monthly, the tier lookup, the auto-select and custom
 * validation are all in `services/consumption.ts`; this component only wires
 * them to `@fluxora/ui` primitives and the funnel store.
 */
export interface ConsumptionSelectorProps {
  /** The meter's estimated annual usage (kWh) — seeds the auto-selection. */
  readonly estimatedAnnualUsageKwh: number;
}

export function ConsumptionSelector({
  estimatedAnnualUsageKwh,
}: ConsumptionSelectorProps): JSX.Element {
  const { t } = useTranslation();
  const funnel = useFunnel();

  const [selectedId, setSelectedId] = useState<ConsumptionSelectionId>('low');
  const [customValue, setCustomValue] = useState<string>('');

  // Auto-select once on mount from the meter's estimated usage.
  useEffect(() => {
    const initial = resolveInitialSelection(estimatedAnnualUsageKwh);
    if (initial.isCustom) {
      setSelectedId(CUSTOM_TIER_ID);
      setCustomValue(String(initial.monthlyKwh));
      funnel.setConsumption(initial.monthlyKwh, CUSTOM_TIER_ID);
    } else if (initial.tier !== null) {
      setSelectedId(initial.tier.id);
      funnel.setConsumption(initial.monthlyKwh, initial.tier.id);
    }
    // Mount-once: a new meter remounts this component (clearResolved unmounts it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseTier = (id: ConsumptionSelectionId): void => {
    setSelectedId(id);
    const tier = USAGE_TIERS.find((candidate) => candidate.id === id);
    if (tier !== undefined) {
      funnel.setConsumption(tier.max, tier.id);
    }
  };

  const chooseCustom = (): void => {
    setSelectedId(CUSTOM_TIER_ID);
    funnel.setConsumption(parseCustomKwh(customValue) ?? undefined, CUSTOM_TIER_ID);
  };

  const onSelectionChange = (value: string): void => {
    if (value === CUSTOM_TIER_ID) chooseCustom();
    else chooseTier(value as ConsumptionSelectionId);
  };

  const onCustomChange = (raw: string): void => {
    setCustomValue(raw);
    funnel.setConsumption(parseCustomKwh(raw) ?? undefined, CUSTOM_TIER_ID);
  };

  const customInvalid = selectedId === CUSTOM_TIER_ID && parseCustomKwh(customValue) === null;

  return (
    <Card variant="glass" padding="md">
      <CardBody className="flex flex-col gap-3 p-0">
        <div className="flex flex-col gap-0.5">
          <h3 className="font-display text-lg font-bold tracking-tight text-foreground">
            {t('shop.consumption.heading')}
          </h3>
          <p className="text-sm text-foreground/70">{t('shop.consumption.subtitle')}</p>
        </div>

        {/* Tier TILES, not a radio column: four same-shape choices scan as a
            row, the selected one wears the pastel brand tint. Still a radio
            group to assistive tech — each tile announces the full tier label
            (word + range) via aria-label. */}
        <div
          role="radiogroup"
          aria-label={t('shop.consumption.heading')}
          className="grid grid-cols-2 gap-2 sm:grid-cols-4"
        >
          {[...USAGE_TIERS.map((tier) => ({ id: tier.id as string, aria: t(tier.nameKey) })),
            { id: CUSTOM_TIER_ID as string, aria: t('shop.consumption.tier.custom') }].map(
            (tile) => {
              const active = selectedId === tile.id;
              return (
                <button
                  key={tile.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={tile.aria}
                  onClick={() => onSelectionChange(tile.id)}
                  className={`flex flex-col items-center justify-center gap-0.5 rounded-[14px] border px-3 py-3 text-center transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    active
                      ? 'border-transparent bg-[image:var(--brand-gradient-tint)] shadow-card'
                      : 'border-border bg-surface hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm'
                  }`}
                >
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-foreground">
                    {active ? (
                      <svg
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                        className="h-3.5 w-3.5"
                      >
                        <path
                          d="M3 8.5 6.5 12 13 4.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : null}
                    {t(`shop.consumption.tier.${tile.id}.short`)}
                  </span>
                  <span
                    className={`text-[11px] leading-tight ${active ? 'text-foreground/75' : 'text-foreground/60'}`}
                  >
                    {t(`shop.consumption.tier.${tile.id}.range`)}
                  </span>
                </button>
              );
            },
          )}
        </div>

        {selectedId === CUSTOM_TIER_ID ? (
          <div className="flex flex-col gap-1">
            <Label htmlFor="consumption-custom-kwh">{t('shop.consumption.custom.label')}</Label>
            <Input
              id="consumption-custom-kwh"
              name="customKwh"
              inputMode="numeric"
              placeholder={t('shop.consumption.custom.placeholder')}
              value={customValue}
              onChange={(e) => onCustomChange(e.target.value)}
              aria-describedby="consumption-custom-help"
              aria-invalid={customInvalid}
            />
            <span id="consumption-custom-help" className="text-xs text-foreground/60">
              {customInvalid
                ? t('shop.consumption.custom.invalid')
                : t('shop.consumption.custom.help')}
            </span>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
